import {
  doc,
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * 색상 검사 메타데이터 로드 (total_results만)
 * - 슬라이드 개수, 총 위반 수, 슬라이드 크기, 브랜드 색상 등
 * @param {string} docId - PPT 문서 ID
 * @returns {Promise<Object|null>} 메타데이터 또는 null
 */
export const loadColorResultsMeta = async (docId) => {
  try {
    console.log('[colorResultService] 문서 읽기: color_results/total_results');
    const resultRef = doc(db, 'PPT_FILE', docId, 'color_results', 'total_results');
    const resultSnap = await getDoc(resultRef);

    if (!resultSnap.exists()) {
      return null;
    }

    const metaData = resultSnap.data();
    return {
      total_slides: metaData.total_slides || 0,
      total_violations: metaData.total_violations || 0,
      brand_colors: metaData.brand_colors || [],
      slide_width: metaData.slide_width || 0,
      slide_height: metaData.slide_height || 0
    };
  } catch (error) {
    console.error('[colorResultService] 메타데이터 로드 오류:', error);
    return null;
  }
};

/**
 * 특정 슬라이드의 색상 검사 결과 로드 (lazy loading)
 * @param {string} docId - PPT 문서 ID
 * @param {number} slideIndex - 슬라이드 인덱스
 * @returns {Promise<Object|null>} 슬라이드 결과 또는 null
 */
export const loadSlideColorResult = async (docId, slideIndex) => {
  try {
    console.log(`[colorResultService] 문서 읽기: color_results/${slideIndex}`);
    const slideRef = doc(db, 'PPT_FILE', docId, 'color_results', slideIndex.toString());
    const slideSnap = await getDoc(slideRef);

    if (!slideSnap.exists()) {
      return null;
    }

    const data = slideSnap.data();
    return {
      slide_index: slideIndex,
      has_violations: data.has_violations || false,
      violations_count: data.violations_count || 0,
      violations: data.violations || []
    };
  } catch (error) {
    console.error(`[colorResultService] 슬라이드 ${slideIndex} 결과 로드 오류:`, error);
    return null;
  }
};

/**
 * 저장된 색상 검사 결과를 로드 (전체)
 * - total_results에서 메타데이터 로드
 * - 개별 슬라이드 문서('0', '1', ...)에서 slide_results 취합
 * @param {string} docId - PPT 문서 ID
 * @returns {Promise<Object|null>} 저장된 검사 결과 또는 null
 */
export const loadColorResults = async (docId) => {
  try {
    // 1. total_results에서 메타데이터 로드
    const metaData = await loadColorResultsMeta(docId);

    if (!metaData || metaData.total_slides === 0) {
      return null;
    }

    const slideCount = metaData.total_slides;

    // 2. 개별 슬라이드 문서에서 slide_results 병렬 로드
    const slidePromises = [];
    for (let i = 0; i < slideCount; i++) {
      slidePromises.push(loadSlideColorResult(docId, i));
    }

    const slideResults = (await Promise.all(slidePromises)).filter(r => r !== null);

    // 3. 메타데이터 + slide_results 합쳐서 반환
    return {
      ...metaData,
      slide_results: slideResults
    };
  } catch (error) {
    console.error('[colorResultService] 색상 결과 로드 오류:', error);
    throw new Error('색상 검사 결과 로드에 실패했습니다.');
  }
};

/**
 * 색상 분석 진행상황 실시간 구독 (color_results 서브컬렉션의 analysis_progress 문서)
 * @param {string} docId - 문서 ID
 * @param {Function} callback - 진행상황 업데이트 콜백
 * @returns {Function} 구독 해제 함수
 */
export const subscribeToColorAnalysisProgress = (docId, callback) => {
  console.log('[colorResultService] 구독 시작: color_results/analysis_progress');
  const colorResultRef = doc(db, 'PPT_FILE', docId, 'color_results', 'analysis_progress');

  return onSnapshot(colorResultRef, { includeMetadataChanges: true }, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();

      callback({
        status: data.status || '',
        percent: data.percent || 0,
        completed: data.completed || false,
        total_violations: data.total_violations || 0,
        error: data.error || null
      });
    } else {
      // 문서가 아직 생성되지 않은 경우
      callback({
        status: '',
        percent: 0,
        completed: false,
        error: null
      });
    }
  }, (error) => {
    console.error('[colorResultService] 색상 분석 진행상황 구독 오류:', error);
    callback({ error: error.message });
  });
};
